/**
 * Named category trees ("dimensions") a question is classified along.
 *
 * A question is reported on several independent axes at once: course faculty
 * track subject mastery, deans and accreditors track licensure readiness. One
 * generic "Category" card hides which of those a question still owes, so each
 * tree is its own dimension with its own required flag and tagging rule.
 *
 * Questions still store one flat `categoryIds` list. A tag's dimension is
 * read from its root node, so blueprint, coverage and import code that reads
 * the flat list keeps working.
 */

import type { DomainDef } from "./defaults"
import { buildCategories } from "./defaults"
import { FRAMEWORK_DIMENSIONS, FRAMEWORK_EXAM_CATEGORIES } from "./frameworks"
import { findCategory, getRootCategoryId } from "./tree"
import type { ExamCategory } from "./types"

/**
 * Who owns a standard, and so who can change its structure.
 *
 *   universal  Bloom's and Difficulty. Set by the product. Every course has them
 *              and nobody edits them.
 *   program    Licensure frameworks, program competencies, accreditation standards.
 *              Owned by the program admin or curriculum committee. Every course
 *              inherits permission to tag with them, never to edit them.
 *   course     What authors tag a question with (course content), and any tree a
 *              course creates for itself: outcomes, lecture index, ad hoc groups.
 *              A tree with `ownerCourseId` belongs to that course and is seen
 *              only there.
 */
export type DimensionTier = "universal" | "program" | "course"

/** Reporting trees need every tag placed on purpose. Coverage trees feed blueprints. */
export type DimensionPurpose = "reporting" | "coverage"

export interface CategoryDimension {
  id: string
  name: string
  description?: string
  /** What sort of standard: accreditation, competencies, outcomes, licensure. */
  kind?: string
  /** Set on a tree a course made for itself. Absent on institution-wide standards. */
  ownerCourseId?: string
  tier: DimensionTier
  purpose: DimensionPurpose
  /** Set by the admin. A required dimension left empty is raised before Ready. */
  required: boolean
  /**
   * Tagging is restricted to a role. The tree stays readable; the author sees
   * it locked, with who can change it, instead of an empty or missing card.
   */
  lockedFor?: { reason: string; unlockedBy: string }
  /**
   * Who may tag a question with this tree. The program director always can.
   * Absent means everyone but a tree with `lockedFor`, which is the director
   * alone: the older way of saying the same thing.
   */
  taggableBy?: TaggingRole[]
  /**
   * The disciplines this standard belongs to, matched against the program name
   * ("nursing" matches "Nursing" and "Nursing (Accelerated)"). Absent means it
   * applies to every program: institutional and program learning outcomes.
   */
  programs?: string[]
}

/** A standard is shown only in the program it belongs to. No program known: all. */
export function appliesToProgram(dimension: CategoryDimension, program?: string): boolean {
  if (!dimension.programs || dimension.programs.length === 0 || !program) return true
  const name = program.toLowerCase()
  return dimension.programs.some((p) => name.includes(p.toLowerCase()))
}

/** The roles a tree's tagging permission is set for. Students never tag. */
export type TaggingRole = "Admin" | "Faculty"

export const BLOOM_DIMENSION_ID = "universal-bloom"
export const DIFFICULTY_DIMENSION_ID = "universal-difficulty"
export const COURSE_CONTENT_DIMENSION_ID = "course-content"
/** Physical therapy keeps its own course content tree; nursing's is not shared with it. */
export const PT_COURSE_CONTENT_DIMENSION_ID = "course-content-pt"
export const NCLEX_DIMENSION_ID = "nclex-client-needs"
export const QSEN_DIMENSION_ID = "qsen-competencies"

/** Mock admin configuration. The real one is set in the tree builder. */
export const DEFAULT_CATEGORY_DIMENSIONS: CategoryDimension[] = [
  {
    id: BLOOM_DIMENSION_ID,
    name: "Bloom's Taxonomy",
    kind: "universal",
    description: "Cognitive level. Set by the product and applied to every course.",
    tier: "universal",
    purpose: "reporting",
    required: false,
    lockedFor: { reason: "Set by the product.", unlockedBy: "Product team" },
  },
  {
    id: DIFFICULTY_DIMENSION_ID,
    name: "Difficulty",
    kind: "universal",
    description: "Easy, Medium or Hard. Set by the product and applied to every course.",
    tier: "universal",
    purpose: "reporting",
    required: false,
    lockedFor: { reason: "Set by the product.", unlockedBy: "Product team" },
  },
  {
    id: COURSE_CONTENT_DIMENSION_ID,
    name: "Course content",
    description: "What the course teaches. Feeds blueprint coverage.",
    programs: ["nursing"],
    tier: "course",
    purpose: "coverage",
    required: true,
  },
  {
    id: PT_COURSE_CONTENT_DIMENSION_ID,
    name: "Course content",
    description: "What the physical therapy courses teach. Private to the program.",
    programs: ["physical therapy"],
    tier: "course",
    purpose: "coverage",
    required: true,
  },
  {
    id: NCLEX_DIMENSION_ID,
    name: "NCLEX-RN Client Needs",
    kind: "licensure",
    description: "Licensure framework the program reports against.",
    programs: ["nursing"],
    tier: "program",
    purpose: "reporting",
    required: true,
  },
  {
    id: QSEN_DIMENSION_ID,
    name: "Program competencies",
    kind: "program_competency",
    description: "QSEN competencies the accreditor audits.",
    programs: ["nursing"],
    tier: "program",
    purpose: "reporting",
    required: false,
    lockedFor: {
      reason: "Competency tags are set by the curriculum committee.",
      unlockedBy: "Program admin",
    },
  },
  ...FRAMEWORK_DIMENSIONS,
]

const NCLEX_DOMAINS: DomainDef[] = [
  {
    id: "nclex-safe-care",
    name: "Safe and Effective Care Environment",
    skills: [
      {
        name: "Management of Care",
        id: "nclex-safe-care-management",
        targets: [
          {
            name: "Safe and Effective Care Environment: Management of Care - Establishing Priorities and Advance Directives",
            id: "nclex-safe-care-management-priorities",
            details: [
              { name: "Establishing priorities", examples: ["Prioritize by acuity and stability"] },
              { name: "Advance directives", examples: ["Honor a documented code status"] },
            ],
          },
          { name: "Delegation and supervision", id: "nclex-safe-care-management-delegation" },
        ],
      },
      { name: "Safety and Infection Control", id: "nclex-safe-care-safety" },
    ],
  },
  {
    id: "nclex-health-promotion",
    name: "Health Promotion and Maintenance",
    skills: [
      { name: "Growth and development", id: "nclex-health-promotion-growth" },
      { name: "Health screening and prevention", id: "nclex-health-promotion-screening" },
    ],
  },
  {
    id: "nclex-psychosocial",
    name: "Psychosocial Integrity",
    skills: [
      { name: "Coping and adaptation", id: "nclex-psychosocial-coping" },
      { name: "Therapeutic communication", id: "nclex-psychosocial-communication" },
    ],
  },
  {
    id: "nclex-physiological",
    name: "Physiological Integrity",
    skills: [
      { name: "Basic Care and Comfort", id: "nclex-physiological-comfort" },
      {
        name: "Pharmacological and Parenteral Therapies",
        id: "nclex-physiological-pharm",
        targets: [
          {
            name: "Pharmacological and Parenteral Therapies: Dosage Calculation & Medication Administration Safety",
            id: "nclex-physiological-pharm-dosage",
            details: [
              { name: "Dosage calculation", examples: ["Weight-based dose by body weight"] },
              { name: "Medication administration safety", examples: ["Verify the order against two identifiers"] },
            ],
          },
          { name: "Expected actions and outcomes", id: "nclex-physiological-pharm-actions" },
        ],
      },
      { name: "Reduction of Risk Potential", id: "nclex-physiological-risk" },
      {
        name: "Physiologic Adaptation",
        id: "nclex-physiological-adaptation",
        targets: [
          {
            name: "Physiologic Adaptation: Pathophysiology of Multisystem Organ Dysfunction Syndrome & Shock",
            id: "nclex-physiological-adaptation-shock",
          },
        ],
      },
    ],
  },
]

const QSEN_DOMAINS: DomainDef[] = [
  { id: "qsen-patient-centered", name: "Patient-centered care", skills: ["Recognize the client's values", "Coordinate care across settings"] },
  { id: "qsen-teamwork", name: "Teamwork and collaboration", skills: ["Communicate in a handoff", "Escalate through the chain of command"] },
  { id: "qsen-evidence", name: "Evidence-based practice", skills: ["Apply a current guideline", "Question a practice with no evidence"] },
  { id: "qsen-quality", name: "Quality improvement", skills: ["Recognize a near miss", "Use unit data to change a practice"] },
  { id: "qsen-safety", name: "Safety", skills: ["Prevent a medication error", "Reduce a fall risk"] },
  { id: "qsen-informatics", name: "Informatics", skills: ["Document in the health record", "Protect client information"] },
]

const PT_CONTENT_DOMAINS: DomainDef[] = [
  { id: "ptc-msk", name: "Musculoskeletal", description: "Examination and treatment of bone, joint, muscle and connective tissue conditions.", skills: ["Examine range of motion and strength", "Apply manual therapy safely", "Prescribe therapeutic exercise", "Manage post-operative orthopedic care"] },
  { id: "ptc-neuro", name: "Neuromuscular", description: "Movement dysfunction from conditions of the nervous system.", skills: ["Examine balance and gait", "Plan care after stroke", "Plan care in spinal cord injury", "Apply motor learning principles"] },
  { id: "ptc-cardiopulm", name: "Cardiovascular and pulmonary", description: "Exercise and airway clearance for heart and lung conditions.", skills: ["Interpret vital signs during exercise", "Prescribe aerobic exercise", "Teach airway clearance", "Recognize signs that stop treatment"] },
  { id: "ptc-integ", name: "Integumentary", description: "Wounds, burns and skin integrity.", skills: ["Examine a wound", "Select a dressing and modality", "Prevent pressure injury"] },
  { id: "ptc-professional", name: "Professional practice and safety", description: "Ethics, documentation and safe practice.", skills: ["Document an episode of care", "Apply infection control", "Act on an ethical dilemma", "Appraise evidence for practice"] },
]

function withDimension(
  domains: DomainDef[],
  dimensionId: string,
): ExamCategory[] {
  return buildCategories(domains).map((category) =>
    category.parentId === null ? { ...category, dimensionId } : category,
  )
}

/**
 * Seeds for the trees beyond course content. Kept out of
 * `DEFAULT_EXAM_CATEGORIES` on purpose: the mock bank is generated from that
 * list, and new roots there would reshuffle every seeded question.
 */
export const DIMENSION_EXAM_CATEGORIES: ExamCategory[] = [
  ...withDimension(NCLEX_DOMAINS, NCLEX_DIMENSION_ID),
  ...withDimension(QSEN_DOMAINS, QSEN_DIMENSION_ID),
  ...withDimension(PT_CONTENT_DOMAINS, PT_COURSE_CONTENT_DIMENSION_ID),
  ...FRAMEWORK_EXAM_CATEGORIES,
]

/** The dimension a category belongs to, read from its root node. */
export function dimensionIdOfCategory(
  categories: ExamCategory[],
  categoryId: string,
): string {
  const rootId = getRootCategoryId(categories, categoryId)
  const root = rootId ? findCategory(categories, rootId) : undefined
  return root?.dimensionId ?? COURSE_CONTENT_DIMENSION_ID
}

/** Every category in one dimension, roots included. */
export function categoriesInDimension(
  categories: ExamCategory[],
  dimensionId: string,
): ExamCategory[] {
  const rootIds = new Set(
    categories
      .filter(
        (category) =>
          category.parentId === null &&
          (category.dimensionId ?? COURSE_CONTENT_DIMENSION_ID) === dimensionId,
      )
      .map((category) => category.id),
  )
  return categories.filter((category) => {
    const rootId = getRootCategoryId(categories, category.id)
    return rootId != null && rootIds.has(rootId)
  })
}

/**
 * A restricted standard can be tagged by the program director (Admin) and by
 * nobody else. Everyone can read it.
 */
export function isDimensionRestricted(
  dimension: CategoryDimension,
  isDirector: boolean,
): boolean {
  return !canTagDimension(dimension, isDirector ? "Admin" : "Faculty")
}

/** The roles that may tag with this tree, the director always among them. */
export function taggingRolesOf(dimension: CategoryDimension): TaggingRole[] {
  if (dimension.tier === "universal") return []
  const base: TaggingRole[] =
    dimension.taggableBy ?? (dimension.lockedFor ? ["Admin"] : ["Admin", "Faculty"])
  return base.includes("Admin") ? base : ["Admin", ...base]
}

export function canTagDimension(
  dimension: CategoryDimension,
  role: "Admin" | "Faculty" | "Student",
): boolean {
  if (role === "Student") return false
  return taggingRolesOf(dimension).includes(role)
}

export type DimensionReading = {
  dimension: CategoryDimension
  /** The question's tags that sit in this dimension. */
  categoryIds: string[]
  /** This person cannot change it. */
  restricted: boolean
  /** Required and still empty, and this person is able to fill it. */
  missing: boolean
}

/** Where a question stands on every dimension, for this person. */
export function readDimensions(
  dimensions: readonly CategoryDimension[],
  categories: ExamCategory[],
  questionCategoryIds: readonly string[],
  options: { isDirector?: boolean } = {},
): DimensionReading[] {
  return dimensions.map((dimension) => {
    const categoryIds = questionCategoryIds.filter(
      (id) => dimensionIdOfCategory(categories, id) === dimension.id,
    )
    const restricted = isDimensionRestricted(dimension, Boolean(options.isDirector))
    return {
      dimension,
      categoryIds,
      restricted,
      missing: dimension.required && !restricted && categoryIds.length === 0,
    }
  })
}

/**
 * Category id to standard id, for every category, in one pass.
 *
 * A list reads the standards of every row it shows, and walking to the root
 * once per row per standard is not affordable on a 1.4K-row bank. This walks
 * each category once.
 */
export function buildDimensionLookup(
  categories: ExamCategory[],
): Map<string, string> {
  const byId = new Map(categories.map((category) => [category.id, category]))
  const lookup = new Map<string, string>()
  const resolve = (id: string): string => {
    const known = lookup.get(id)
    if (known) return known
    const category = byId.get(id)
    const dimensionId = !category
      ? COURSE_CONTENT_DIMENSION_ID
      : category.parentId
        ? resolve(category.parentId)
        : (category.dimensionId ?? COURSE_CONTENT_DIMENSION_ID)
    lookup.set(id, dimensionId)
    return dimensionId
  }
  for (const category of categories) resolve(category.id)
  return lookup
}

/** Required standards this person could fill and the question has not. */
export function missingRequiredStandards(
  lookup: ReadonlyMap<string, string>,
  dimensions: readonly CategoryDimension[],
  questionCategoryIds: readonly string[],
  options: { isDirector?: boolean } = {},
): CategoryDimension[] {
  const have = new Set(
    questionCategoryIds.map(
      (id) => lookup.get(id) ?? COURSE_CONTENT_DIMENSION_ID,
    ),
  )
  return dimensions.filter(
    (dimension) =>
      dimension.required &&
      !isDimensionRestricted(dimension, Boolean(options.isDirector)) &&
      !have.has(dimension.id),
  )
}

/**
 * The categories a question can be tagged with: only the question-level
 * standards (the course content tree). Program-level standards stay in
 * Settings and never appear in a picker an author uses.
 */
export function questionLevelCategories(
  categories: ExamCategory[],
  questionDimensions: readonly CategoryDimension[],
): ExamCategory[] {
  const allowed = new Set(questionDimensions.map((dimension) => dimension.id))
  const lookup = buildDimensionLookup(categories)
  return categories.filter((category) =>
    allowed.has(lookup.get(category.id) ?? COURSE_CONTENT_DIMENSION_ID),
  )
}

/** A standard with its categories, as the reports and course pages read it. */
export type StandardTree = {
  id: string
  name: string
  kind?: string
  nodes: Array<ExamCategory & { frameworkId: string }>
}

/** Every program-level standard and its tree. Course content is not one: it is what authors tag. */
export function programStandardTrees(
  categories: ExamCategory[],
  dimensions: readonly CategoryDimension[],
): StandardTree[] {
  return dimensions
    .filter((dimension) => dimension.tier === "program")
    .map((dimension) => ({
      id: dimension.id,
      name: dimension.name,
      kind: dimension.kind,
      nodes: categoriesInDimension(categories, dimension.id).map((node) => ({
        ...node,
        frameworkId: dimension.id,
      })),
    }))
    .filter((tree) => tree.nodes.length > 0)
}

// ─── Ownership ──────────────────────────────────────────────────────────────

/** A tree a single course made for itself. */
export function isCourseOwned(dimension: CategoryDimension): boolean {
  return dimension.tier === "course" && Boolean(dimension.ownerCourseId)
}

/** The institution-wide tree authors tag with: course content. */
export function contentDimensions(
  dimensions: readonly CategoryDimension[],
): CategoryDimension[] {
  return dimensions.filter((d) => d.tier === "course" && !d.ownerCourseId)
}

export function programDimensions(
  dimensions: readonly CategoryDimension[],
): CategoryDimension[] {
  return dimensions.filter((d) => d.tier === "program")
}

/** The trees a course owns. Nobody else sees them by default. */
export function courseTreesFor(
  dimensions: readonly CategoryDimension[],
  courseId: string | undefined,
): CategoryDimension[] {
  if (!courseId) return []
  return dimensions.filter((d) => d.tier === "course" && d.ownerCourseId === courseId)
}

/**
 * What a course gets without asking: every universal and program standard, to
 * tag with. Tag access, not edit access: the structure stays with the owner.
 */
export function inheritedDimensionsFor(
  dimensions: readonly CategoryDimension[],
): CategoryDimension[] {
  return dimensions.filter((d) => d.tier === "universal" || d.tier === "program")
}

/** Who may change a tree's structure. */
export function dimensionOwnerLabel(dimension: CategoryDimension): string {
  if (dimension.tier === "universal") return "Product team"
  if (dimension.tier === "program") return "Program admin"
  return dimension.ownerCourseId ? "Course coordinator" : "Program admin"
}

/** Can this person change the structure of this tree? */
export function canEditDimensionStructure(
  dimension: CategoryDimension,
  who: { isDirector: boolean; managesCourseId?: string },
): boolean {
  if (dimension.tier === "universal") return false
  if (dimension.ownerCourseId) {
    return who.isDirector || who.managesCourseId === dimension.ownerCourseId
  }
  return who.isDirector
}
